import type {
	CreateResponsibleOrganizationBody,
	ListResponsibleOrganizationsQuery,
	Paginated,
	ResponsibleOrganizationListItem,
	ResponsibleOrganizationOption,
	ResponsibleOrganizationStats,
	UpdateResponsibleOrganizationBody,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryClient } from "../api/query-client";
import { scopeOptionsOptions } from "../users/queries";

export const responsibleOrganizationsKeys = {
	all: ["responsible-organizations"] as const,
	list: (q: ListResponsibleOrganizationsQuery) => ["responsible-organizations", "list", q] as const,
	options: ["responsible-organizations", "options"] as const,
	stats: ["responsible-organizations", "stats"] as const,
};

/** One request → page + total + every row's usage counts (the API aggregates in SQL). */
export const responsibleOrganizationsListOptions = (q: ListResponsibleOrganizationsQuery) =>
	queryOptions({
		queryKey: responsibleOrganizationsKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<ResponsibleOrganizationListItem>>("/responsible-organizations", { query: q, signal }),
		// Keep the previous page on screen while the next loads — no blank flash.
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/** The whole list, for `<select>` elements — admin-curated, tens of rows. */
export const responsibleOrganizationOptionsOptions = () =>
	queryOptions({
		queryKey: responsibleOrganizationsKeys.options,
		queryFn: ({ signal }) => api.get<ResponsibleOrganizationOption[]>("/responsible-organizations/options", { signal }),
		staleTime: 5 * 60_000,
	});

/** The stats strip and the type chips' counts — one aggregate request, unfiltered. Under `all`, so a write refreshes it. */
export const responsibleOrganizationStatsOptions = () =>
	queryOptions({
		queryKey: responsibleOrganizationsKeys.stats,
		queryFn: ({ signal }) => api.get<ResponsibleOrganizationStats>("/responsible-organizations/stats", { signal }),
		staleTime: 60_000,
	});

/** Also the user scope editor's dropdowns (`/users/scope-options` lists responsible organizations) — else a new or renamed one is missing there for 5 minutes. */
export const invalidateResponsibleOrganizations = () =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: responsibleOrganizationsKeys.all }),
		queryClient.invalidateQueries({ queryKey: scopeOptionsOptions().queryKey }),
	]);

export const createResponsibleOrganization = (body: CreateResponsibleOrganizationBody) => api.post<ResponsibleOrganizationListItem>("/responsible-organizations", body);

export const updateResponsibleOrganization = (id: string, body: UpdateResponsibleOrganizationBody) => api.patch<ResponsibleOrganizationListItem>(`/responsible-organizations/${id}`, body);

export const deleteResponsibleOrganization = (id: string) => api.delete<null>(`/responsible-organizations/${id}`);
